import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { CheckCircle2, Clock, XCircle, HeartPulse } from "lucide-react";
import { cn } from "../../lib/utils";

const POSTPONE_OPTIONS = [
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
  { label: "3 months", days: 90 },
];

const PulseRequestApproval = ({ request, isOpen, onClose, onDecision }) => {
  const [mode, setMode] = useState("main");
  const [selectedPostponeDays, setSelectedPostponeDays] = useState(14);

  const handleDecision = (decision, postponeDays) => {
    onDecision(decision, postponeDays);
    setMode("main");
  };

  const handleOpenChange = (open) => {
    if (!open) {
      setMode("main");
      onClose();
    }
  };

  const cadenceLabel =
    {
      annual: "Annual",
      quarterly: "Quarterly",
      biannual: "Bi-annual",
    }[request?.cadence] || "Scheduled";

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-background max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HeartPulse className="w-5 h-5 text-primary" />
            Governance Pulse Request
          </DialogTitle>
        </DialogHeader>

        {mode === "main" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              A {cadenceLabel} Governance Pulse assessment has been scheduled.
              Please decide how you'd like to proceed.
            </p>
            <div className="bg-muted/30 rounded-lg p-3 text-xs text-muted-foreground space-y-1.5">
              <div>
                Cadence:{" "}
                <span className="font-medium text-foreground">
                  {cadenceLabel}
                </span>
              </div>
              <div>
                Estimated time per member:{" "}
                <span className="font-medium text-foreground">~20 min</span>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => handleDecision("approved")}
                className="w-full justify-start gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                Approve — Start the assessment now
              </Button>
              <Button
                variant="outline"
                onClick={() => setMode("postpone")}
                className="w-full justify-start gap-2"
              >
                <Clock className="w-4 h-4" />
                Postpone — Delay to a later date
              </Button>
              <Button
                variant="ghost"
                onClick={() => handleDecision("declined")}
                className="w-full justify-start gap-2 text-destructive hover:text-destructive"
              >
                <XCircle className="w-4 h-4" />
                Decline — Skip this assessment
              </Button>
            </div>
          </div>
        )}

        {mode === "postpone" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Choose how long to postpone the Governance Pulse assessment.
            </p>
            <div className="grid grid-cols-2 gap-2">
              {POSTPONE_OPTIONS.map((opt) => (
                <button
                  key={opt.days}
                  onClick={() => setSelectedPostponeDays(opt.days)}
                  className={cn(
                    "p-3 rounded-lg border text-sm transition-all",
                    selectedPostponeDays === opt.days
                      ? "border-primary bg-primary/5 text-foreground font-medium"
                      : "border-border hover:border-primary/40 text-muted-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setMode("main")}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() =>
                  handleDecision("postponed", selectedPostponeDays)
                }
                className="flex-1"
              >
                Confirm postponement
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PulseRequestApproval;
